import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Layers, Search, SearchX } from 'lucide-react';
import SchemaModal from '../../../ui/SchemaModal';
import Tabs from '../../../ui/Tabs';
import Input from '../../../ui/Input';
import CardGrid from '../../../ui/CardGrid';
import CardImage from '../../../ui/CardImage';
import EmptyState from '../../../ui/EmptyState';
import Skeleton from '../../../ui/Skeleton';
import { apiClient } from '../../../api/apiClient';
import { errorMessage } from '../../../api/errorMessage';
import './TokenPicker.css';

const MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;

/** Eintrag aus `tokensAvailable` (Deck oder schon gesucht) als Anzeige-Daten. */
const fromAvailable = (token) => ({
  cardId: token.cardId,
  name: token.scryfallData?.name || token.name || 'Token',
  imageUrl: token.scryfallData?.imageUrl || token.imageUrl || '',
  imageUrlBack: token.scryfallData?.imageUrlBack || token.imageUrlBack || '',
});

/**
 * Token zum Erzeugen auswählen.
 *
 *   <TokenPicker open={open} onOpenChange={setOpen} available={me.tokensAvailable}
 *     onPick={(cardId, tokenData) => { if (tokenData) addTokenToAvailable(tokenData); createToken(cardId, pos); }} />
 *
 * - `SchemaModal size="lg"` mit zwei Reitern: „Aus dem Deck" (`available`, die Token-Liste des
 *   Teilnehmers) und „Suche" (`GET /api/playtest/search-tokens?q=`, ab 2 Zeichen, 300 ms nach
 *   der letzten Eingabe). Ohne Deck-Token öffnet der Picker auf „Suche".
 * - Ein Klick auf ein Bild ruft `onPick(tokenCardId, tokenData)` und schließt den Picker.
 *   `tokenData` ist nur gesetzt, wenn der Token aus der Suche kommt und noch nicht in `available`
 *   steht: der Server erzeugt nur Token aus der Liste des Teilnehmers, die Seite muss ihn deshalb
 *   vor dem Erzeugen mit `addTokenToAvailable(tokenData)` aufnehmen.
 * - Raster `CardGrid variant="browse"` mit `CardImage`; der Name steht unter dem Bild, nichts liegt
 *   über der unteren Bildkante.
 * - Eine spätere Antwort überholt nie eine neuere Eingabe; Schließen setzt Reiter und Suche zurück.
 */
export default function TokenPicker({ open, onOpenChange, available = [], onPick }) {
  const hasDeckTokens = available.length > 0;
  const [tab, setTab] = useState(hasDeckTokens ? 'deck' : 'search');
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState({ status: 'idle', results: [], error: null });
  const requestRef = useRef(0);

  useEffect(() => {
    if (open) {
      setTab(hasDeckTokens ? 'deck' : 'search');
    } else {
      requestRef.current += 1;
      setQuery('');
      setSearch({ status: 'idle', results: [], error: null });
    }
    // Der Start-Reiter wird nur beim Öffnen bestimmt; ein Token, der währenddessen in die
    // Liste kommt, soll den Reiter nicht wechseln.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const term = query.trim();
    const requestId = ++requestRef.current;
    if (term.length < MIN_QUERY_LENGTH) {
      setSearch({ status: 'idle', results: [], error: null });
      return undefined;
    }
    setSearch((prev) => ({ ...prev, status: 'loading', error: null }));
    const timer = setTimeout(async () => {
      try {
        const { data } = await apiClient.get(`/api/playtest/search-tokens?q=${encodeURIComponent(term)}`);
        if (requestRef.current !== requestId) return;
        setSearch({ status: 'done', results: Array.isArray(data) ? data : [], error: null });
      } catch (err) {
        if (requestRef.current !== requestId) return;
        setSearch({ status: 'error', results: [], error: errorMessage(err, 'Die Token-Suche ist fehlgeschlagen.') });
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const pick = (cardId, tokenData) => {
    onPick?.(cardId, tokenData);
    onOpenChange?.(false);
  };

  const pickSearchResult = (result) => {
    const known = available.some((token) => token.cardId === result.cardId);
    pick(result.cardId, known ? undefined : result);
  };

  return (
    <SchemaModal open={open} onOpenChange={onOpenChange} size="lg" title="Token erzeugen">
      <Tabs value={tab} onValueChange={setTab}>
        <Tabs.List>
          <Tabs.Trigger value="deck" icon={Layers}>Aus dem Deck</Tabs.Trigger>
          <Tabs.Trigger value="search" icon={Search}>Suche</Tabs.Trigger>
        </Tabs.List>

        <Tabs.Panel value="deck">
          {hasDeckTokens ? (
            <TokenGrid tokens={available.map(fromAvailable)} onPick={(token) => pick(token.cardId)} />
          ) : (
            <EmptyState
              icon={Layers}
              title="Das Deck bringt keine Token mit"
              description="Über die Suche findest du jeden Token."
              action={(
                <button type="button" className="btn-primary" onClick={() => setTab('search')}>
                  Token suchen
                </button>
              )}
            />
          )}
        </Tabs.Panel>

        <Tabs.Panel value="search">
          <div className="token-picker__search">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onClear={() => setQuery('')}
              placeholder="Token-Name, mindestens 2 Zeichen"
              aria-label="Token suchen"
              autoFocus
            />
            <SearchResults search={search} query={query} onPick={pickSearchResult} />
          </div>
        </Tabs.Panel>
      </Tabs>
    </SchemaModal>
  );
}

function SearchResults({ search, query, onPick }) {
  if (search.status === 'loading') {
    return (
      <CardGrid variant="browse">
        {[0, 1, 2].map((i) => (
          <div key={i} className="token-picker__skeleton">
            <Skeleton height="100%" radius="lg" label={i === 0 ? 'Token werden gesucht' : undefined} />
          </div>
        ))}
      </CardGrid>
    );
  }
  if (search.status === 'error') {
    return <EmptyState variant="error" icon={AlertTriangle} title={search.error} />;
  }
  if (search.status === 'done' && search.results.length === 0) {
    return (
      <EmptyState
        variant="filter"
        icon={SearchX}
        title="Kein Token gefunden"
        description={`Für „${query.trim()}" gibt es keinen Token.`}
      />
    );
  }
  if (search.status === 'idle') {
    return <EmptyState size="compact" icon={Search} title="Namen eingeben, um Token zu finden" />;
  }
  return <TokenGrid tokens={search.results} onPick={onPick} />;
}

function TokenGrid({ tokens, onPick }) {
  return (
    <CardGrid variant="browse">
      {tokens.map((token) => (
        <figure key={token.cardId} className="token-picker__token" data-testid="token-picker-token">
          <CardImage
            src={token.imageUrl || ''}
            backSrc={token.imageUrlBack || ''}
            alt={token.name}
            onOpen={() => onPick(token)}
            openLabel={`${token.name} erzeugen`}
          />
          <figcaption className="token-picker__name">{token.name}</figcaption>
        </figure>
      ))}
    </CardGrid>
  );
}
